<template>
  <div
    id="playposit-audio-recorder"
    :class="[{
      'is-focused-by-keyboard': isFocusedByKeyboard,
    }, browserName.toLowerCase(), 'playposit-audio-recorder']"
  >
    <v-toolbar
      color="#2196F3"
      class="elevation-1"
    >
      <v-layout
        justify-center
        align-center
        class="full-height"
      >
        <v-flex
          xs1
          class="full-height"
        >
          <v-layout
            class="full-height full-width"
            align-center
            justify-center
          >
            <v-btn
              v-if="audio"
              @click="resetAudioRecording({ resetAudioRecorder: true })"
              :disabled="isProcessingData"
              depressed
              :ripple="false"
              color="#EF5350"
              block
              dark
              class="audio-recorder-playback-button br-0"
            >
              <v-icon color="white">
                delete
              </v-icon>
            </v-btn>
            <v-btn
              v-else-if="!isRecordingAudio"
              @click="startRecordingAudio"
              :disabled="isProcessingData"
              flat
              :ripple="false"
              color="white"
              block
              class="audio-recorder-playback-button"
            >
              <v-icon>fiber_manual_record</v-icon>
            </v-btn>
            <v-btn
              v-else
              @click="stopRecordingAudio"
              :disabled="isProcessingData"
              flat
              :ripple="false"
              color="white"
              block
              class="audio-recorder-playback-button"
            >
              <v-icon>stop</v-icon>
            </v-btn>
          </v-layout>
        </v-flex>
        <v-flex
          xs10
          class="full-height full-width"
        >
          <v-layout
            class="full-height blue darken-1 relative"
            align-center
            justify-center
          >
            <div
              v-if="!hasRecordedAudio"
              class="audio-recorder-message"
            >
              Click the record button on the left to begin recording audio.
            </div>
            <div
              v-if="(
                !audio
                && isRecordingAudio
              )"
              class="audio-recorder-spinner"
            >
              <div class="double-bounce1"></div>
              <div class="double-bounce2"></div>
            </div>
            <div v-else-if="!audio" />
            <PlaypositAudioPlayer
              v-else
              :src="audio"
            />
          </v-layout>
        </v-flex>
        <v-flex
          xs1
          class="full-height"
          align-center
        >
          <v-layout
            class="full-height full-width"
            align-center
            justify-center
          >
            <v-btn
              v-if="audio"
              @click="uploadAudio"
              depressed
              :loading="isProcessingData"
              :ripple="false"
              color="#66BB6A"
              block
              dark
              class="audio-recorder-playback-button br-0"
            >
              <v-icon color="white">
                done
              </v-icon>
            </v-btn>
            <v-layout
              v-else
              class="full-height full-width title"
              :class="[isRecordingAudio ? 'white--text' : 'disabled-text']"
              justify-center
              align-center
            >
              {{ remainingDuration }}
            </v-layout>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-toolbar>
    <v-alert
      :value="!!errorState"
      type="error"
      class="ma-0 audio-recorder-alert"
      :icon="errorIcons[errorState] || 'warning'"
    >
      <template v-if="errorState === 'NotAllowedError'">
        Permission to record audio was denied.
        <br/>
        <a
          href="https://knowledge.playposit.com/article/189-audio-recording"
          class="error-link white--text underline"
          target="_blank"
        >
          Click here to learn how to grant permission in your browser.
        </a>
      </template>
      <template v-else-if="errorState === 'NotFoundError'">
        Unable to detect a microphone.
      </template>
      <template v-else-if="errorState === 'UnsupportedApiError'">
        Your browser does not support media recording at this time. <span v-html="chromeRecommendationMessage"></span>
      </template>
      <template v-else>
        An unspecified recording error has occurred. <span v-html="chromeRecommendationMessage"></span>
      </template>
    </v-alert>
  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import PlaypositAudioPlayer from './components/AudioPlayer.vue';
import parser from './js/userAgentParser';

/*
  Dependencies (NOT peerDependencies) imported via require() CANNOT just point to the name of the NPM package. For some reason, api-dev/api-stage will attempt to look for that package inside `components/audioRecorder/src` instead of `components/audioRecorder/node_modules`.
*/
const RecordRTC = require('../node_modules/recordrtc');

export default {
  name: 'PlaypositAudioRecorder',
  components: {
    PlaypositAudioPlayer,
  },
  props: {
    serverUrl: {
      type: String,
      validator: (serverUrl) => (
        typeof serverUrl === 'string'
        || serverUrl === null
      ),
    },
    token: {
      required: true,
      validator: (token) => (
        typeof token === 'string'
        || token === null
      ),
    },
    maxDuration: {
      type: Number,
      default: 90000,
    },
  },
  data: () => ({
    isFocusedByKeyboard: false,
    userAgent: null,
    hasRecordedAudio: false,
    isRecordingAudio: false,
    audioRecorder: null,
    audioStream: null,
    audio: null,
    recordedAudioBlob: null,
    recordingDurationInterval: null,
    remainingDuration: 0,
    isProcessingData: false,
    errorState: null,
    errorIcons: {
      NotAllowedError: 'not_interested',
      NotFoundError: 'volume_off',
    },
  }),
  computed: {
    browserName() {
      return this?.userAgent?.browser?.name;
    },
    defaultRemainingDuration() {
      return this.maxDuration / 1000;
    },
    localServerUrl() {
      return `${this.serverUrl}${this.serverUrl.endsWith('/') ? '' : '/'}`;
    },
    remainingDurationDisplay() {
      return this.remainingDuration.toHoursMinutesSeconds();
    },
    chromeRecommendationMessage() {
      if (this.browserName !== 'Chrome') {
        return 'Try using <a href="https://www.google.com/chrome/" class="error-link white--text" target="_blank">Google Chrome</a> for a more feature-rich PlayPosit experience.';
      }
      return '';
    },
    eventListeners() {
      return {
        keydown: this.handleKeyDown,
        mousedown: this.handleMouseDown,
      };
    },
  },
  methods: {
    handleKeyDown(e) {
      if (e.keyCode === 9) this.$set(this, 'isFocusedByKeyboard', true);
    },
    handleMouseDown() {
      this.$set(this, 'isFocusedByKeyboard', false);
    },
    enableEventListeners() {
      for (const key of Object.keys(this.eventListeners)) {
        document.addEventListener(key, this.eventListeners[key]);
      }
    },
    disableEventListeners() {
      for (const key of Object.keys(this.eventListeners)) {
        document.removeEventListener(key, this.eventListeners[key]);
      }
    },
    handleAudioInitializationError(error) {
      if (error === 'Your browser does not supports Media Recorder API. Please try other modules e.g. WhammyRecorder or StereoAudioRecorder.') {
        this.errorState = 'UnsupportedApiError';
      } else {
        this.errorState = error.name;
      }
      throw error;
    },
    async initializeAudioRecording() {
      try {
        this.errorState = null;
        const audioStream = (
          navigator.mediaDevices.getUserMedia
          && !navigator.getUserMedia
        )
          ? await navigator.mediaDevices.getUserMedia({ audio: true })
          : await new Promise((resolve, reject) => navigator.getUserMedia({ audio: true }, resolve, reject));
        this.isAudioRecordingEnabled = true;
        this.audioStream = audioStream;
        this.audioRecorder = RecordRTC(audioStream, {
          type: 'audio',
          recorderType: RecordRTC.StereoAudioRecorder,
          disableLogs: true,
        });
        this.audioRecorder
          .setRecordingDuration(this.maxDuration)
          .onRecordingStopped(this.stopRecordingAudio);
        return this.audioRecorder;
      } catch(e) {
        return this.handleAudioInitializationError(e);
      }
    },
    resetAudioRecording({
      resetAudioRecorder = false,
    } = {}) {
      if (
        resetAudioRecorder
        && _.isFunction(this?.audioRecorder?.reset)
      ) {
        this.audioRecorder.reset();
      }
      this.audio = null;
      this.recordedAudioBlob = null;
      this.isRecordingAudio = false;
      this.remainingDuration = this.defaultRemainingDuration;
    },
    toggleRecordingDurationInterval() {
      if (!this.recordingDurationInterval) {
        this.recordingDurationInterval = setInterval(() => {
          if (this.remainingDuration > 0) this.remainingDuration -= 1;
        }, 1000);
      } else {
        clearInterval(this.recordingDurationInterval);
        this.recordingDurationInterval = null;
        this.remainingDuration = this.defaultRemainingDuration;
      }
    },
    async startRecordingAudio() {
      if (!this.audioStream) await this.initializeAudioRecording();
      this.isRecordingAudio = true;
      this.hasRecordedAudio = true;
      this.audioRecorder.startRecording();
    },
    stopRecordingAudio() {
      if (_.isFunction(this.audioVisualizer?.stop)) this.audioVisualizer.stop();
      this.resetAudioRecording();
      this.isRecordingAudio = false;
      if (!this.audioRecorder) return;
      this.audioRecorder.stopRecording((audioVideoWebMURL) => {
        this.audio = audioVideoWebMURL;
        this.recordedAudioBlob = this.audioRecorder.getBlob();
        if (this.audioStream) {
          this.audioStream.getTracks().forEach((track) => {
            if (_.isFunction(track.stop)) track.stop();
          });
        }
        this.audioStream = null;
      });
    },
    async uploadAudio() {
      try {
        this.isProcessingData = true;
        const formData = new FormData();
        formData.append('file', this.recordedAudioBlob);
        const { data: audioData } = await axios.post(`${this.localServerUrl}upload/audio`, formData);
        this.$emit('success', audioData.audioUrl);
      } catch(e) {
        this.$emit('error', e);
      } finally {
        this.isProcessingData = false;
      }
    },
  },
  watch: {
    isRecordingAudio: 'toggleRecordingDurationInterval',
    isProcessingData(newValue) {
      this.$emit('isProcessingData', newValue);
    },
  },
  beforeDestroy() {
    this.stopRecordingAudio();
    this.disableEventListeners();
  },
  created() {
    this.userAgent = parser();
    this.remainingDuration = this.defaultRemainingDuration;
  },
  async mounted() {
    await this.$nextTick();
    this.disableEventListeners();
    this.enableEventListeners();
  },
};
</script>

<style lang="scss">
  #playposit-audio-recorder {
    position: relative;

    &.firefox {
      .playposit-audio-player {
        .playposit-audio-player-displayed-durations {
          top: -17px;
        }
        .volume-slider {
          top: 45% !important;
        }
      }
    }

    &.is-focused-by-keyboard {
      button:focus {
        outline: 2px solid #FD9727 !important;
      }
      .playposit-audio-player .playposit-range-input:focus::-webkit-slider-thumb{
        outline: 2px solid #FD9727 !important;
      }
      .playposit-audio-player .playposit-range-input:focus::-moz-range-thumb {
        outline: 2px solid #FD9727 !important;
      }
    }

    > .v-toolbar > .v-toolbar__content {
      padding: 0;
    }

    .audio-recorder-playback-button {
      height: 100%;
      min-width: 0;
    }

    .disabled-text {
      color: rgba(0,0,0,0.26) !important
    }

    .audio-recorder-message {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #F5F5F5;
      font-size: 14px;
      max-width: 85%;
      margin-left: auto;
      margin-right: auto;
    }

    .audio-recorder-alert {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      font-size: 12px;
    }

    .audio-recorder-spinner {
      width: 40px;
      height: 40px;

      position: relative;
      margin: 100px auto;
    }

    .double-bounce1,
    .double-bounce2 {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: #FFF;
      opacity: 0.6;
      position: absolute;
      top: 0;
      left: 0;

      -webkit-animation: sk-bounce 2.0s infinite ease-in-out;
      animation: sk-bounce 2.0s infinite ease-in-out;
    }

    .double-bounce2 {
      -webkit-animation-delay: -1.0s;
      animation-delay: -1.0s;
    }

    @-webkit-keyframes sk-bounce {
      0%, 100% { -webkit-transform: scale(0.0) }
      50% { -webkit-transform: scale(1.0) }
    }

    @keyframes sk-bounce {
      0%, 100% {
        transform: scale(0.0);
        -webkit-transform: scale(0.0);
      } 50% {
        transform: scale(1.0);
        -webkit-transform: scale(1.0);
      }
    }
  }
</style>